import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ChangeDetectionStrategy, Component, ContentChild, ElementRef, Input, TemplateRef, ViewChild, forwardRef } from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from "@angular/forms";
import { BYTES_IN_MEGABYTE, DEFAULT_DEBOUNCE_TIME, FunctionUtils, LocalComponentStore } from "@dtm-frontend/shared/utils";
import { TranslocoService } from "@jsverse/transloco";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ContentChange } from "ngx-quill/lib/quill-editor.component";
import { ToastrService } from "ngx-toastr";
import QuillType from "quill";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";

interface QuillEditorComponentState {
    hasErrors: boolean;
    placeholder: string;
    isTableFormatterAvailable: boolean;
    isFontColorFormatterAvailable: boolean;
    isLinkFormatterAvailable: boolean;
}

const MAX_PICTURE_SIZE_IN_BYTES = BYTES_IN_MEGABYTE;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png"];

@UntilDestroy()
@Component({
    selector: "dtm-ui-quill-editor",
    templateUrl: "./quill-editor.component.html",
    styleUrls: ["./quill-editor.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        LocalComponentStore,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QuillEditorComponent),
            multi: true,
        },
    ],
})
export class QuillEditorComponent implements ControlValueAccessor {
    @Input() public set hasError(value: BooleanInput) {
        this.localStore.patchState({ hasErrors: coerceBooleanProperty(value) });
    }
    @Input() public set placeholder(value: string | undefined) {
        this.localStore.patchState({ placeholder: value ?? "" });
    }
    @Input() public set isTableFormatterAvailable(value: BooleanInput) {
        this.localStore.patchState({ isTableFormatterAvailable: coerceBooleanProperty(value) });
    }
    @Input() public set isFontColorFormatterAvailable(value: BooleanInput) {
        this.localStore.patchState({ isFontColorFormatterAvailable: coerceBooleanProperty(value) });
    }
    @Input() public set isLinkFormatterAvailable(value: BooleanInput) {
        this.localStore.patchState({ isLinkFormatterAvailable: coerceBooleanProperty(value) });
    }
    @ViewChild("fileInputField") protected fileInputField!: ElementRef<HTMLInputElement>;

    @ContentChild("customToolbarTemplate") protected customToolbarTemplate: TemplateRef<HTMLElement> | undefined;

    protected quillEditorRef!: QuillType;
    protected ACCEPTED_IMAGE_TYPES = ACCEPTED_IMAGE_TYPES;
    protected control = new FormControl<string | null>(null);
    protected hasErrors$ = this.localStore.selectByKey("hasErrors");
    protected placeholder$ = this.localStore.selectByKey("placeholder");
    protected isTableFormatterAvailable$ = this.localStore.selectByKey("isTableFormatterAvailable");
    protected isFontColorFormatterAvailable$ = this.localStore.selectByKey("isFontColorFormatterAvailable");
    protected isLinkFormatterAvailable$ = this.localStore.selectByKey("isLinkFormatterAvailable");

    private propagateTouch = FunctionUtils.noop;
    private propagateChange: (value: string | null) => void = FunctionUtils.noop;
    private readonly valueChange$ = new Subject<string | null>();

    constructor(
        private readonly localStore: LocalComponentStore<QuillEditorComponentState>,
        private readonly toastrService: ToastrService,
        private readonly translocoService: TranslocoService
    ) {
        localStore.setState({
            hasErrors: false,
            placeholder: "",
            isTableFormatterAvailable: false,
            isFontColorFormatterAvailable: false,
            isLinkFormatterAvailable: false,
        });

        this.valueChange$.pipe(debounceTime(DEFAULT_DEBOUNCE_TIME), untilDestroyed(this)).subscribe((value: string | null) => {
            this.propagateChange(value);
        });
    }

    public registerOnChange(fn: (value: string | null) => void): void {
        this.propagateChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.propagateTouch = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.control.disable();
        } else {
            this.control.enable();
        }
    }

    public writeValue(value: string | null): void {
        this.control.setValue(value, { emitEvent: false });
    }

    protected handleEditorCreation(editorInstance: QuillType) {
        this.quillEditorRef = editorInstance;
        const toolbar = editorInstance.getModule("toolbar");
        toolbar.addHandler("image", this.imageHandler.bind(this));
    }

    protected updateControl(editorInstance: ContentChange) {
        this.valueChange$.next(editorInstance.html);

        if (this.control.value === editorInstance.html) {
            return;
        }
        this.control.setValue(editorInstance.html);

        // NOTE: value is set to "" to allow uploading the same file more than once
        this.fileInputField.nativeElement.value = "";
    }

    protected markAsTouched() {
        this.propagateTouch();
    }

    private imageHandler() {
        const input = this.fileInputField.nativeElement;

        input.onchange = () => {
            const inputFiles = input.files ?? [];
            if (!inputFiles.length) {
                return;
            }

            const file = inputFiles[0];
            if (!/^image\/png/.test(file.type) && !/^image\/jpeg/.test(file.type)) {
                this.toastrService.error(this.translocoService.translate("dtmUi.quillEditor.onlyImageCanBeUploadedError"));

                return;
            }

            if (file.size > MAX_PICTURE_SIZE_IN_BYTES) {
                this.toastrService.error(
                    this.translocoService.translate("dtmUi.quillEditor.chosenImageIsTooBigError", {
                        maxFileSizeInMegabytes: MAX_PICTURE_SIZE_IN_BYTES / BYTES_IN_MEGABYTE,
                    })
                );

                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                const range = this.quillEditorRef.getSelection();
                const img = `<img src="${reader.result}" />`;
                this.quillEditorRef.clipboard.dangerouslyPasteHTML(range?.index ?? 0, img);
            };
            reader.readAsDataURL(file);
        };

        input.click();
    }
}
